import React, { forwardRef } from 'react';

interface InputProps {
  type?: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  step?: string;
  min?: string;
  max?: string;
  error?: boolean;
  defaultValue?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  type = 'text',
  placeholder,
  value,
  onChange,
  className = '',
  required = false,
  disabled = false,
  name,
  id,
  autoComplete,
  autoFocus = false,
  step,
  min,
  max,
  error = false,
  defaultValue,
}, ref) => {
  const baseClasses = `
    w-full px-4 py-3 
    text-gray-900 dark:text-gray-100 text-sm
    bg-white dark:bg-gray-800
    ${error 
      ? 'border-2 border-red-500 dark:border-red-500' 
      : 'border-2 border-gray-200 dark:border-gray-600'}
    rounded-xl
    placeholder-gray-400 dark:placeholder-gray-500
    transition-all duration-200 ease-in-out
    focus:outline-none 
    ${error 
      ? 'focus:border-red-600 dark:focus:border-red-400 focus:ring-4 focus:ring-red-100 dark:focus:ring-red-900' 
      : 'focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900'}
    hover:border-gray-300 dark:hover:border-gray-500
    disabled:bg-gray-50 dark:disabled:bg-gray-700
    disabled:text-gray-500 dark:disabled:text-gray-400
    disabled:border-gray-200 dark:disabled:border-gray-600
    disabled:cursor-not-allowed
  `.replace(/\s+/g, ' ').trim();

  return (
    <input
      ref={ref}
      type={type}
      placeholder={placeholder}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      className={`${baseClasses} ${className}`}
      required={required}
      disabled={disabled}
      name={name}
      id={id}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      step={step}
      min={min}
      max={max}
    />
  );
});

Input.displayName = 'Input';
